import React, { useState, useEffect, useMemo } from 'react';
import { string, bool, any } from 'prop-types';
import { useProductDeprecated } from '@thd-nucleus/data-sources/react/dataModel/migration';
import { useStore } from '@thd-nucleus/experience-context';
import { useLayoutGroupProps } from '@thd-olt-component-react/layout-manager';
import { PromoModelProvider } from '../contexts/PromoModelProvider';
import { PromoCartProvider } from '../contexts/PromoCartProvider';
import { PromoPresentationProvider } from '../contexts/PromoPresentationProvider';
import { PromoDrawer } from './promo-drawer/PromoDrawer';
import { PromoCallToAction } from './promo-call-to-action/PromoCallToAction';
import { isValidPromotionData, isSanitaryItemId } from '../utils/promo-model-utils';
import { dataModel } from './dataModel';

import './PromotionProducts.scss';

const returnProductIfValid = (data) => (
  (data?.product?.pricing?.promotion
  && isValidPromotionData(data.product.pricing)
  && isSanitaryItemId(data?.product.itemId))
    ? data.product
    : null
);

const shouldDisplayPromotion = (bogoEnabled, msbEnabled, bmsmEnabled, anchorItem) => {
  const experienceTag = anchorItem?.pricing?.promotion?.experienceTag;
  return (bogoEnabled && experienceTag === 'BOGO')
      || (msbEnabled && experienceTag === 'MSBONLINE')
      || (bmsmEnabled && experienceTag === 'BMSM');
};

export const PromotionProducts = ({
  itemId, productDataModel, ...props
}) => {

  const {
    bogoEnabled, msbEnabled, bmsmEnabled
  } = useLayoutGroupProps({ props });

  useEffect(() => {
    if (bogoEnabled || msbEnabled || bmsmEnabled) {
      LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('promotion-products.ready');
    }
  }, [bogoEnabled, msbEnabled, bmsmEnabled]);

  const store = useStore();
  const [renderPromoDrawer, setRenderPromoDrawer] = useState(false);
  const [isBatchLoading, setIsBatchLoading] = useState(true);

  const { data } = useProductDeprecated({
    itemId,
    storeId: store.storeId,
    domains: ['$ProductPod'],
    ssr: false,
    skip: (!bogoEnabled && !msbEnabled && !bmsmEnabled) || !isSanitaryItemId(itemId),
  }, 'clientOnlyProduct');

  const anchorItem = useMemo(() => returnProductIfValid(data), [data]);

  const renderDrawer = () => setRenderPromoDrawer(true);

  return shouldDisplayPromotion(bogoEnabled, msbEnabled, bmsmEnabled, anchorItem) ? (
    <PromoModelProvider
      anchorItem={anchorItem}
      store={store}
      setIsBatchLoading={setIsBatchLoading}
      productDataModel={productDataModel}
    >
      <PromoCartProvider>
        <PromoPresentationProvider isBatchLoading={isBatchLoading} renderPromoDrawer={renderPromoDrawer}>
          <PromoCallToAction
            onPromoCtaHover={renderDrawer}
            isInPromoDrawer={false}
          />
          {renderPromoDrawer && (
            <PromoDrawer />
          )}
        </PromoPresentationProvider>
      </PromoCartProvider>
    </PromoModelProvider>
  ) : null;
};

PromotionProducts.displayName = 'PromotionProducts';

PromotionProducts.propTypes = {
  itemId: string,
  /* eslint-disable-next-line react/no-unused-prop-types */
  bogoEnabled: bool,
  /* eslint-disable-next-line react/no-unused-prop-types */
  msbEnabled: bool,
  /* eslint-disable-next-line react/no-unused-prop-types */
  bmsmEnabled: bool,
  // eslint-disable-next-line react/forbid-prop-types
  productDataModel: any,
};

PromotionProducts.defaultProps = {
  itemId: '',
  bogoEnabled: true,
  msbEnabled: true,
  bmsmEnabled: false,
  productDataModel: {},
};

PromotionProducts.dataModel = dataModel;
